import { HttpStatus } from '@montugroup/apis-styleguide';
import type { GetWomDiscountCodeResponseType } from '@montugroup/pms-api-contracts';
import { apiPatientPortalContract } from '@montugroup/pms-api-contracts';
import { useMutation } from '@tanstack/react-query';
import { TsRestResponseError } from '@ts-rest/core';

import { Logger } from '@/utils/logger';
import { useSession } from '@montugroup/circuit-shared';
import { getTsPatientPortalClient } from '@montugroup/circuit-shared/api/patient-portal.client';

const logger = new Logger('useGenerateDiscountCode');
const client = getTsPatientPortalClient();

/**
 * Hook that requests generation of a discount code
 *
 * @returns Fetcher function, status variables, returned data
 */
export function useGenerateDiscountCode() {
  const { session } = useSession();

  const generateDiscountCodeMutation = useMutation<GetWomDiscountCodeResponseType, Error, string | undefined>({
    mutationKey: ['generate-discount-code'],
    mutationFn: async (patientCode?: string) => {
      // check for user token
      const token = session?.token;
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await client.getWomDiscountCode.mutate({
        body: patientCode ? { patientCode } : undefined,
      });

      if (response.status !== HttpStatus.OK && response.status !== HttpStatus.CREATED) {
        throw new TsRestResponseError(apiPatientPortalContract.getWomDiscountCode, response);
      }

      return response.body.data;
    },
    retry: false,
    onSuccess: (data) => {
      logger.info('Discount code generated successfully:', data);
    },
    onError: (error) => {
      logger.error(`Error generating discount code:`, {
        error,
        client: 'ts-rest',
      });
    },
  });

  const generateDiscountCode = async (patientCode?: string) => {
    try {
      return await generateDiscountCodeMutation.mutateAsync(patientCode);
    } catch (error) {
      logger.error('Error in generateDiscountCode:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to generate discount code');
    }
  };

  return {
    generateDiscountCode,
    isPending: generateDiscountCodeMutation.isPending,
    isError: generateDiscountCodeMutation.isError,
    isSuccess: generateDiscountCodeMutation.isSuccess,
    data: generateDiscountCodeMutation.data,
  };
}

export default useGenerateDiscountCode;
